"use client";

import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  Suspense,
  lazy,
} from "react";

// Lazy-load Jodit for plain React projects (no next/dynamic)
const JoditEditor = lazy(() => import("jodit-react"));

// optional CSS for Jodit (uncomment if you want default styles)
// import "jodit/build/jodit.min.css";

const EVENTS_API_URL = "https://service.ireedindia.com/v1/events";
const UPLOAD_API_URL = "https://service.ireedindia.com/upload";
const REPLACE_API_URL = "https://service.ireedindia.com/replace-files";

type Location = {
  city: string;
  state: string;
  country: string;
  pincode: string;
};

type YoutubeItem = {
  YoutubeHeading: string;
  Youtubelink: string;
};

type EventForm = {
  title: string;
  SubTitle: string;
  banner: string; // filename only
  thumbNail: string; // filename only
  galleryText: string; // newline separated filenames
  map: string;
  slug: string;
  content: string;
  description: string;
  subDescription: string;
  userId: string;
  altTag: string;
  date: string; // YYYY-MM-DD
  published: boolean;
  metaTitle: string;
  metaDesc: string;
  metaKeyword: string;
  location: Location;
  Youtube: YoutubeItem[];
};

type EventItem = {
  _id?: string;
  [key: string]: any; // be flexible with API shape
};

const initialFormState: EventForm = {
  title: "",
  SubTitle: "",
  banner: "",
  thumbNail: "",
  galleryText: "",
  map: "",
  slug: "",
  content: "",
  description: "",
  subDescription: "",
  userId: "",
  altTag: "",
  date: "",
  published: false,
  metaTitle: "",
  metaDesc: "",
  metaKeyword: "",
  location: {
    city: "",
    state: "",
    country: "",
    pincode: "",
  },
  Youtube: [{ YoutubeHeading: "", Youtubelink: "" }],
};

const inputClass =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-300";
const textareaClass =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-indigo-300";

// -------- helpers ----------

// extract filename from upload / replace response (support multiple shapes)
const extractFilename = (data: any): string => {
  if (data === null || data === undefined) return "";

  // plain string
  if (typeof data === "string") return data;

  // array (take first)
  if (Array.isArray(data)) {
    const first = data[0];
    return extractFilename(first);
  }

  // object shapes: check common keys
  return (
    data?.newFilename ||
    data?.newFileName ||
    data?.newfilename ||
    data?.filename ||
    data?.fileName ||
    data?.name ||
    data?.savedFilename ||
    // some APIs return uploadedFiles: ['a.jpg']
    (Array.isArray(data?.uploadedFiles) && data.uploadedFiles[0]) ||
    (typeof data?.uploadedFiles === "string" && data.uploadedFiles) ||
    ""
  );
};

// "YYYY-MM-DD" -> "July 25th 2024"
const formatDisplayDate = (isoDate: string): string => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  const dateObj = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(dateObj.getTime())) return isoDate;

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const d = dateObj.getDate();
  const suffix =
    d % 10 === 1 && d !== 11
      ? "st"
      : d % 10 === 2 && d !== 12
        ? "nd"
        : d % 10 === 3 && d !== 13
          ? "rd"
          : "th";

  return `${monthNames[dateObj.getMonth()]} ${d}${suffix} ${year}`;
};

// "July 25th 2024" -> "2024-07-25" (best effort)
const parseDisplayDateToIso = (display: string): string => {
  if (!display) return "";
  const date = new Date(display);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const EventsManagement: React.FC = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [token, setToken] = useState<string>("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState<EventForm>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // delete confirm
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // upload states
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [replacingGalleryIndex, setReplacingGalleryIndex] = useState<number | null>(
    null
  );
  const [deletingGalleryIndex, setDeletingGalleryIndex] = useState<number | null>(
    null
  );

  // preview URLs (from URL.createObjectURL)
  const [bannerPreview, setBannerPreview] = useState<string>("");
  const [thumbPreview, setThumbPreview] = useState<string>("");
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);

  // publishing state (for individual event)
  const [publishingId, setPublishingId] = useState<string | null>(null);

  const addGalleryInputRef = useRef<HTMLInputElement | null>(null);
  const descEditorRef = useRef<any>(null);
  const subDescEditorRef = useRef<any>(null);

  // description validation UI state
  const [descriptionError, setDescriptionError] = useState<string | null>(null);

  // -------- load token + userId from localStorage.userDetails ----------
  useEffect(() => {
    if (typeof window === "undefined") return;

    const userDetailsStr = localStorage.getItem("userDetails");
    if (!userDetailsStr) return;

    try {
      const userDetails = JSON.parse(userDetailsStr);
      if (userDetails.accessToken) setToken(userDetails.accessToken);

      if (userDetails.userId) {
        setForm((prev) => ({ ...prev, userId: userDetails.userId }));
      }
    } catch (err) {
      console.error("Failed to parse userDetails", err);
    }
  }, []);

  // -------- fetch events ----------
  useEffect(() => {
    if (!token) return;

    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(EVENTS_API_URL, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) throw new Error("Failed to fetch events");

        const data = await res.json();
        let list: any[] = [];
        if (Array.isArray(data)) list = data;
        else if (Array.isArray(data.data)) list = data.data;
        else if (Array.isArray(data.data?.events)) list = data.data.events;
        else if (Array.isArray(data.events)) list = data.events;

        setEvents(list);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch events");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [token]);

  // -------- jodit config ----------
  const placeholder = "Start typing...";
  const joditConfig = useMemo(
    () => ({
      readonly: false,
      placeholder,
      toolbarAdaptive: false,
      toolbarSticky: false,
      uploader: {
        insertImageAsBase64URI: true,
      },
      // removeButtons: ['image'],
      disablePlugins: "drag-and-drop-element",
      controls: {
        image: {
          exec: function (editor: any) {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/*";
            input.onchange = function () {
              const file = (input as HTMLInputElement).files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = function (e) {
                // insert base64 image
                const src = (e.target as any).result;
                try {
                  if (
                    editor.selection &&
                    typeof editor.selection.insertImage === "function"
                  ) {
                    editor.selection.insertImage(src);
                  } else if (
                    editor.selection &&
                    typeof editor.selection.insertNode === "function"
                  ) {
                    const img = editor.createInside.element("img");
                    img.setAttribute("src", src);
                    editor.selection.insertNode(img);
                  } else {
                    editor.selection.insertHTML(
                      `<img src="${src}" alt="Inserted image" />`
                    );
                  }
                } catch (err) {
                  editor.selection.insertHTML(
                    `<img src="${src}" alt="Inserted image" />`
                  );
                }
              };
              reader.readAsDataURL(file);
            };
            input.click();
          },
          icon: "image",
          tooltip: "Insert Image",
        },
      },
      style: {
        textAlign: "left",
      },
    }),
    [placeholder]
  );

  // -------- upload helpers ----------
  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch(UPLOAD_API_URL, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || "Upload failed");
    }

    const data = await res.json();
    const filename = extractFilename(data);
    if (!filename) throw new Error("Upload succeeded but filename missing");
    return filename;
  };

  const replaceFile = async (oldFilename: string, file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    // backend variations: support both keys
    formData.append("oldFilenames", oldFilename);
    formData.append("oldFilename", oldFilename);

    const res = await fetch(REPLACE_API_URL, {
      method: "PATCH",
      body: formData,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || "Replace failed");
    }

    const data = await res.json();

    // prefer newFilename if backend returns both old/new
    let filename = "";
    if (data && typeof data === "object") {
      filename =
        data?.newFilename || data?.newFileName || data?.newfilename || extractFilename(data);
    } else {
      filename = extractFilename(data);
    }

    if (!filename) throw new Error("Replace succeeded but filename missing");
    return filename;
  };

  // -------- file change handlers (with local previews) ----------

  const handleBannerFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);

    try {
      setUploadingBanner(true);
      const filename = form.banner ? await replaceFile(form.banner, file) : await uploadFile(file);
      setForm((prev) => ({ ...prev, banner: filename }));
      setBannerPreview(localUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload/replace banner image");
      URL.revokeObjectURL(localUrl);
    } finally {
      setUploadingBanner(false);
      e.target.value = "";
    }
  };

  const handleThumbFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);

    try {
      setUploadingThumb(true);
      const filename = form.thumbNail ? await replaceFile(form.thumbNail, file) : await uploadFile(file);
      setForm((prev) => ({ ...prev, thumbNail: filename }));
      setThumbPreview(localUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload/replace thumbnail image");
      URL.revokeObjectURL(localUrl);
    } finally {
      setUploadingThumb(false);
      e.target.value = "";
    }
  };

  const handleGalleryAddFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    try {
      setUploadingGallery(true);

      const currentNames = form.galleryText ? form.galleryText.split("\n").filter(Boolean) : [];
      const currentPreviews = [...galleryPreviews];

      for (const file of files) {
        const localUrl = URL.createObjectURL(file);
        const filename = await uploadFile(file);
        currentNames.push(filename);
        currentPreviews.push(localUrl);
      }

      setForm((prev) => ({
        ...prev,
        galleryText: currentNames.join("\n"),
      }));
      setGalleryPreviews(currentPreviews);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload gallery images");
    } finally {
      setUploadingGallery(false);
      e.target.value = "";
    }
  };

  const handleGalleryReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const currentNames = form.galleryText ? form.galleryText.split("\n").filter(Boolean) : [];
    const oldFilename = currentNames[index];
    if (!oldFilename) return;

    const localUrl = URL.createObjectURL(file);

    try {
      setReplacingGalleryIndex(index);
      const newFilename = await replaceFile(oldFilename, file);
      currentNames[index] = newFilename;

      const newPreviews = [...galleryPreviews];
      newPreviews[index] = localUrl;

      setForm((prev) => ({
        ...prev,
        galleryText: currentNames.join("\n"),
      }));
      setGalleryPreviews(newPreviews);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to replace gallery image");
      URL.revokeObjectURL(localUrl);
    } finally {
      setReplacingGalleryIndex(null);
      e.target.value = "";
    }
  };

  // -------- new: delete gallery image ----------
  const handleGalleryDelete = async (index: number) => {
    const currentNames = form.galleryText ? form.galleryText.split("\n").filter(Boolean) : [];
    const filename = currentNames[index];
    if (!filename) return;

    // confirm with user
    const ok = window.confirm("Delete this gallery image permanently?");
    if (!ok) return;

    const deleteUrl = `https://service.ireedindia.com/delete-file/${filename}`;

    try {
      setDeletingGalleryIndex(index);
      const res = await fetch(deleteUrl, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to delete image");
      }

      // remove from lists and revoke preview URL if present
      const newNames = currentNames.filter((_, i) => i !== index);
      const newPreviews = galleryPreviews.filter((_, i) => i !== index);

      // revoke the removed preview URL if it was an object URL
      const removedPreview = galleryPreviews[index];
      if (removedPreview && removedPreview.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(removedPreview);
        } catch (err) {
          // ignore
        }
      }

      setForm((prev) => ({ ...prev, galleryText: newNames.join("\n") }));
      setGalleryPreviews(newPreviews);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete image");
    } finally {
      setDeletingGalleryIndex(null);
    }
  };

  // -------- form helpers ----------

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === "city" || name === "state" || name === "country" || name === "pincode") {
      setForm((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          [name]: value,
        },
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePublishedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, published: e.target.checked }));
  };

  const handleYoutubeChange = (index: number, field: keyof YoutubeItem, value: string) => {
    setForm((prev) => {
      const updated = [...prev.Youtube];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, Youtube: updated };
    });
  };

  const handleAddYoutube = () => {
    setForm((prev) => ({ ...prev, Youtube: [...prev.Youtube, { YoutubeHeading: "", Youtubelink: "" }] }));
  };

  const handleRemoveYoutube = (index: number) => {
    setForm((prev) => ({ ...prev, Youtube: prev.Youtube.filter((_, i) => i !== index) }));
  };

  const resetForm = () => {
    setForm((prev) => ({ ...initialFormState, userId: prev.userId }));
    setBannerPreview("");
    setThumbPreview("");
    setGalleryPreviews([]);
    setEditingId(null);
    setDescriptionError(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // -------- EDIT handling ----------

  const handleEditClick = (event: EventItem) => {
    const root = (event as any).event ?? event;

    const location: Location =
      Array.isArray(root.location) && root.location.length > 0
        ? root.location[0]
        : root.location || {
          city: root.city || "",
          state: root.state || "",
          country: root.country || "",
          pincode: root.pincode || "",
        };

    const gallery = Array.isArray(root.gallery)
      ? root.gallery
      : typeof root.gallery === "string"
        ? [root.gallery]
        : [];

    setForm((prev) => ({
      ...prev,
      title: root.title || root.eventTitle || root.name || "",
      SubTitle: root.SubTitle || root.subTitle || "",
      banner: root.banner || "",
      thumbNail: root.thumbNail || root.thumbnail || "",
      galleryText: gallery.join("\n"),
      map: root.map || "",
      slug: root.slug || "",
      content: root.content || "",
      description: root.description || "",
      subDescription: root.subDescription || "",
      userId: root.userId || prev.userId,
      altTag: root.altTag || "",
      date: parseDisplayDateToIso(root.date || ""),
      published:
        typeof root.published === "boolean" ? root.published : root.status === "published",
      metaTitle: root.metaTitle || "",
      metaDesc: root.metaDesc || "",
      metaKeyword: root.metaKeyword || "",
      location,
      Youtube: Array.isArray(root.Youtube) && root.Youtube.length > 0 ? root.Youtube : [{ YoutubeHeading: "", Youtubelink: "" }],
    }));

    // previews for existing images will be blank until user uploads new ones
    setBannerPreview("");
    setThumbPreview("");
    setGalleryPreviews(new Array(gallery.length).fill(""));

    setEditingId(root._id || root.id || null);
    setDescriptionError(null);
    setIsModalOpen(true);
  };

  // -------- CREATE / UPDATE submit ----------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // client-side validation for description (Jodit doesn't accept HTML 'required' prop)
    if (!form.description || form.description.trim() === "" || form.description === "<p><br></p>") {
      setDescriptionError("Description is required.");
      setIsSubmitting(false);
      return;
    }

    setDescriptionError(null);

    if (!token) {
      setError("Login required. Please login again.");
      setIsSubmitting(false);
      return;
    }

    const galleryArray = form.galleryText
      .split("\n")
      .map((g) => g.trim())
      .filter(Boolean);

    const payload = {
      title: form.title,
      SubTitle: form.SubTitle,
      banner: form.banner,
      thumbNail: form.thumbNail,
      gallery: galleryArray,
      map: form.map,
      slug: form.slug,
      content: form.content,
      description: form.description,
      subDescription: form.subDescription,
      userId: form.userId,
      altTag: form.altTag,
      date: formatDisplayDate(form.date),
      published: form.published,
      Youtube: form.Youtube,
      metaTitle: form.metaTitle,
      metaDesc: form.metaDesc,
      metaKeyword: form.metaKeyword,
      location: [form.location],
    };

    const method = editingId ? "PATCH" : "POST";
    const url = editingId ? `${EVENTS_API_URL}/${editingId}` : EVENTS_API_URL;

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "Failed to save event");
      }

      const saved = await res.json();

      if (editingId) {
        setEvents((prev) => prev.map((ev) => (ev._id ?? ev.id) === (saved._id ?? saved.id) ? saved : ev));
      } else {
        setEvents((prev) => [saved, ...prev]);
      }

      closeModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong while saving");
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------- TOGGLE PUBLISH / UNPUBLISH ----------
  const handleTogglePublish = async (id?: string, currentPublished?: boolean) => {
    if (!id) return;
    if (!token) {
      setError("Login required. Please login again.");
      return;
    }

    const newPublished = !currentPublished;
    setPublishingId(id);
    setError(null);

    // optimistic update
    setEvents((prev) => prev.map((ev) => ((ev._id ?? ev.id) === id ? { ...ev, published: newPublished } : ev)));

    try {
      // prefer PATCH to update published flag. Backend might accept { published: true }
      const res = await fetch(`${EVENTS_API_URL}/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ published: newPublished }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to update publish status");
      }

      const updated = await res.json();

      // ensure server response wins
      setEvents((prev) => prev.map((ev) => ((ev._id ?? ev.id) === (updated._id ?? updated.id) ? updated : ev)));
    } catch (err) {
      // rollback optimistic update on error
      setEvents((prev) => prev.map((ev) => ((ev._id ?? ev.id) === id ? { ...ev, published: currentPublished } : ev)));
      setError(err instanceof Error ? err.message : "Failed to update publish status");
    } finally {
      setPublishingId(null);
    }
  };

  // -------- DELETE ----------
  const handleDeleteClick = (id?: string) => {
    if (!id) return;
    setDeleteId(id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    if (!token) {
      setError("Login required. Please login again.");
      return;
    }

    try {
      const res = await fetch(`${EVENTS_API_URL}/${deleteId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "Failed to delete event");
      }

      setEvents((prev) => prev.filter((ev) => (ev._id ?? ev.id) !== deleteId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong while deleting");
    } finally {
      setShowConfirm(false);
      setDeleteId(null);
    }
  };

  // -------- render helpers ----------
  const galleryFilenames = form.galleryText ? form.galleryText.split("\n").filter(Boolean) : [];

  const getRowTitle = (ev: EventItem): string => ev.title || ev.eventTitle || ev.name || "-";
  const getRowDate = (ev: EventItem): string => ev.date || ev.eventDate || "";
  const getRowLocation = (ev: EventItem): string => {
    const loc = Array.isArray(ev.location) ? ev.location[0] : ev.location;
    if (!loc) return "-";
    const city = loc.city || "";
    const state = loc.state || "";
    if (!city && !state) return "-";
    return city && state ? `${city}, ${state}` : city || state;
  };

  const isRowPublished = (ev: EventItem): boolean => typeof ev.published === "boolean" ? ev.published : ev.status === "published";

  // -------- RENDER UI ----------
  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-xl font-semibold">Events Management</h1>
          <p className="text-gray-500 mt-1">Manage your events efficiently</p>
        </div>

        <button
          onClick={openCreateModal}
          className="text-white font-medium px-4 py-2 rounded-full bg-gradient-to-r from-indigo-600 via-indigo-500 to-pink-500 shadow-lg"
        >
          + Add New Event
        </button>
      </div>

      {/* List Card */}
      <div className="bg-white rounded-lg p-4 shadow-md">
        {loading && <p>Loading events...</p>}
        {error && <p className="text-red-600 mb-2 text-sm">{error}</p>}

        {!loading && events.length === 0 && <p className="text-gray-500">No events found.</p>}

        {events.length > 0 && (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left bg-gray-50 border-b border-gray-200">
                <th className="p-3">Title</th>
                <th className="p-3">Date</th>
                <th className="p-3">Location</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => {
                const id = ev._id || ev.id;
                const published = isRowPublished(ev);

                return (
                  <tr key={id} className="border-b border-gray-200">
                    <td className="p-3">{getRowTitle(ev)}</td>
                    <td className="p-3">{getRowDate(ev)}</td>
                    <td className="p-3">{getRowLocation(ev)}</td>
                    <td className="p-3">
                      <span className={`px-2 py-[2px] rounded-full text-xs ${published ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {published ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-2">
                      <button onClick={() => handleEditClick(ev)} className="bg-sky-100 text-sky-700 rounded-md px-3 py-1 text-xs">
                        Edit
                      </button>
                      <button onClick={() => handleDeleteClick(id)} className="bg-red-100 text-red-700 rounded-md px-3 py-1 text-xs">
                        Delete
                      </button>

                      {/* Publish/Unpublish button */}
                      <button
                        onClick={() => handleTogglePublish(id, published)}
                        disabled={publishingId === id}
                        className={`${published ? 'bg-yellow-100 text-yellow-700' : 'bg-green-50 text-green-700'} rounded-md px-3 py-1 text-xs`}
                      >
                        {publishingId === id ? (published ? "Unpublishing..." : "Publishing...") : (published ? "Unpublish" : "Publish")}
                      </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900/40 flex justify-center items-start pt-10 z-50">
          <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-lg p-6 overflow-y-auto shadow-xl mx-4">
            <div className="flex justify-between mb-4 items-center">
              <h2 className="text-lg font-semibold">{editingId ? "Edit Event" : "Create Event"}</h2>
              <button onClick={closeModal} className="bg-transparent text-xl cursor-pointer">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* basic info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Title *</label>
                  <input name="title" value={form.title} onChange={handleInputChange} required className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Sub Title *</label>
                  <input name="SubTitle" value={form.SubTitle} onChange={handleInputChange} className={inputClass} />
                </div>

                {/* banner */}
                <div>
                  <label className="block text-sm font-medium mb-1">Banner Image *</label>
                  <input type="file" accept="image/*" onChange={handleBannerFileChange} className={"w-full px-1 py-1 text-sm"} />
                  <div className="text-xs text-gray-500 mt-1">
                    {uploadingBanner ? "Uploading banner..." : form.banner ? `Current banner filename: ${form.banner}` : "Choose a banner image"}
                  </div>
                  {bannerPreview && <img src={bannerPreview} alt="Banner preview" className="mt-1 max-w-full max-h-[120px] object-cover rounded-md border border-gray-200" />}
                </div>

                {/* thumbnail */}
                <div>
                  <label className="block text-sm font-medium mb-1">Thumbnail Image *</label>
                  <input type="file" accept="image/*" onChange={handleThumbFileChange} className={"w-full px-1 py-1 text-sm"} />
                  <div className="text-xs text-gray-500 mt-1">
                    {uploadingThumb ? "Uploading thumbnail..." : form.thumbNail ? `Current thumbnail filename: ${form.thumbNail}` : "Choose a thumbnail image"}
                  </div>
                  {thumbPreview && <img src={thumbPreview} alt="Thumbnail preview" className="mt-1 max-w-full max-h-[120px] object-cover rounded-md border border-gray-200" />}
                </div>

                {/* date / slug / alt / map */}
                <div>
                  <label className="block text-sm font-medium mb-1">Date *</label>
                  <input type="date" name="date" value={form.date} onChange={handleInputChange} className={inputClass} />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Slug *</label>
                  <input name="slug" value={form.slug} onChange={handleInputChange} placeholder="doranda-college-ranchi-career-workshop-recruitment-drive" required className={inputClass} />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Alt Tag *</label>
                  <input name="altTag" value={form.altTag} onChange={handleInputChange} className={inputClass} />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Map (Embed URL)</label>
                  <input name="map" value={form.map} onChange={handleInputChange} className={inputClass} />
                </div>
              </div>

              {/* location */}
              <h3 className="mt-6 mb-1 text-base font-medium">Location</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">City</label>
                  <input name="city" value={form.location.city} onChange={handleInputChange} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State</label>
                  <input name="state" value={form.location.state} onChange={handleInputChange} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Country</label>
                  <input name="country" value={form.location.country} onChange={handleInputChange} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Pincode</label>
                  <input name="pincode" value={form.location.pincode} onChange={handleInputChange} className={inputClass} />
                </div>
              </div>

              {/* content */}
              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Short Content (Heading)</label>
                <input name="content" value={form.content} onChange={handleInputChange} placeholder="About The Event" className={inputClass} />
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium mb-1">Main Description *</label>
                {/* Jodit Editor for description */}
                <div className="mt-1">
                  <Suspense fallback={<div>Loading editor...</div>}>
                    <div className="border rounded-lg min-h-[220px] overflow-hidden">
                      <JoditEditor
                        ref={descEditorRef}
                        value={form.description}
                        config={joditConfig}
                        onBlur={(newContent: string) => {
                          setForm((prev) => ({ ...prev, description: newContent }));
                          if (!newContent || newContent.trim() === "" || newContent === "<p><br></p>") {
                            setDescriptionError("Description is required.");
                          } else {
                            setDescriptionError(null);
                          }
                        }}
                        onChange={() => { }}
                        className="h-full"
                      />
                    </div>
                  </Suspense>
                </div>
                {descriptionError && <div className="text-red-600 text-sm mt-1">{descriptionError}</div>}
              </div>

              {/*<div style={{ marginTop: "12px" }}>
                <label>Sub Description</label>
                
                <div style={{ marginTop: 6 }}>
                  <Suspense fallback={<div>Loading editor...</div>}>
                    <JoditEditor
                      ref={subDescEditorRef}
                      value={form.subDescription}
                      config={joditConfig}
                      onBlur={(newContent: string) => setForm((prev) => ({ ...prev, subDescription: newContent }))}
                      onChange={() => {}}
                      className="border rounded-lg"
                    />
                  </Suspense>
                </div>
              </div>*/}
              <div className="mt-3">
                <label className="block text-sm font-medium mb-1">Sub Description</label>
                <textarea
                  name="subDescription"
                  value={form.subDescription}
                  onChange={handleInputChange}
                  rows={3}
                  className={textareaClass}
                />
              </div>


              {/* gallery */}
              <div className="mt-4 flex space-x-2 justify-start items-center">
                <label className="block text-sm font-medium mb-1 text-start">Gallery Images</label>

                <input ref={addGalleryInputRef} type="file" multiple accept="image/*" onChange={handleGalleryAddFiles} className="hidden" />

                <button type="button" onClick={() => addGalleryInputRef.current?.click()} className="rounded-full px-4 py-1 border-dashed border test border-gray-300 bg-gray-50 text-sm mt-1">
                  + Add Photos
                </button>

                {/* <div className="text-xs text-gray-500 mt-1">
                  {uploadingGallery ? "Uploading gallery images..." : !galleryFilenames.length ? "No gallery images added yet." : "You can replace or delete any image below."}
                </div> */}

                {galleryFilenames.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {galleryFilenames.map((filename, index) => (
                      <div key={`${filename}-${index}`} className="border border-gray-200 rounded-md p-2 bg-gray-50 flex flex-col items-center gap-1">
                        {galleryPreviews[index] ? (
                          <img src={galleryPreviews[index]} alt={`Gallery ${index + 1}`} className="w-full h-20 object-cover rounded-md bg-gray-200" />
                        ) : (
                          <div className="w-full h-20 rounded-md bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                            No Preview
                          </div>
                        )}

                        <div className="text-xs text-center break-words">{filename}</div>

                        <div className="flex gap-2 w-full">
                          <label className="rounded-full px-3 py-1 bg-sky-100 text-sky-700 text-xs cursor-pointer flex-1 text-center">
                            {replacingGalleryIndex === index ? "Replacing..." : "Replace Image"}
                            <input type="file" accept="image/*" onChange={(e) => handleGalleryReplaceFile(e, index)} className="hidden" />
                          </label>

                          <button type="button" onClick={() => handleGalleryDelete(index)} disabled={deletingGalleryIndex === index} className="rounded-full px-3 py-1 bg-red-100 text-red-700 text-xs w-full">
                            {deletingGalleryIndex === index ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* YouTube */}
              <h3 className="mt-6 mb-2 text-base text-start font-medium">YouTube Videos</h3>
              {form.Youtube.map((yt, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                  <input placeholder="Heading" value={yt.YoutubeHeading} onChange={(e) => handleYoutubeChange(index, "YoutubeHeading", e.target.value)} className={inputClass} />
                  <input placeholder="Embed URL" value={yt.Youtubelink} onChange={(e) => handleYoutubeChange(index, "Youtubelink", e.target.value)} className={inputClass} />
                  <button type="button" onClick={() => handleRemoveYoutube(index)} className="bg-red-100 text-red-700 rounded-md px-3 py-2 text-sm">
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" onClick={handleAddYoutube} className="border-dashed border border-gray-300 bg-gray-50 rounded-md px-3 py-2 text-sm mb-3">
                + Add YouTube
              </button>

              {/* SEO */}
              <h3 className="mt-6 mb-2 text-base font-medium">SEO Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Meta Title</label>
                  <input name="metaTitle" value={form.metaTitle} onChange={handleInputChange} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Meta Keywords</label>
                  <input name="metaKeyword" value={form.metaKeyword} onChange={handleInputChange} className={inputClass} />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium mb-1">Meta Description</label>
                <textarea name="metaDesc" value={form.metaDesc} onChange={handleInputChange} rows={3} className={textareaClass} />
              </div>

              {/* status + actions */}
              <div className="mt-4 flex justify-between items-center gap-3">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.published} onChange={handlePublishedChange} className="form-checkbox h-4 w-4 text-indigo-600" />
                  <span className="text-sm">Published</span>
                </label>

                <div className="flex gap-2">
                  <button type="button" onClick={closeModal} className="rounded-full px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className={`rounded-full px-4 py-2 text-white ${isSubmitting ? 'opacity-70' : ''} bg-gradient-to-r from-indigo-600 via-indigo-500 to-pink-500`}>
                    {isSubmitting ? (editingId ? "Updating..." : "Saving...") : editingId ? "Update Event" : "Save Event"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-md w-[320px] shadow-lg">
            <h3 className="mb-2 text-base font-medium">Confirm Delete</h3>
            <p className="mb-4 text-sm">Are you sure you want to delete this event?</p>

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowConfirm(false)} className="px-3 py-1 rounded-md border border-gray-300 bg-white">
                Cancel
              </button>

              <button onClick={confirmDelete} className="px-3 py-1 rounded-md bg-red-600 text-white">
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsManagement;
